/**
 * Role → landing route + role labelling (v3.56.0).
 *
 * Before this module, post-auth navigation funnelled EVERYONE to
 * `/dashboard` (the Teacher Dashboard) from three separate `<Navigate>`s,
 * and `/dashboard` then re-derived the admin/TLS branch inline. A user
 * provisioned from a Clever `staff` role therefore landed on the teacher
 * dashboard and saw "Assignment Pending — no active classes are linked to
 * your account", which is the correct empty state for a role that
 * legitimately has no classes and the wrong page entirely.
 *
 * Landing routes are now resolved HERE, once, and the sidebar takes its
 * label from the same resolution.
 *
 * Scope: routing and labelling only. These helpers never widen or narrow
 * what a role may DO — Firestore rules and custom-claim semantics are
 * untouched. A user who can reach a route today can still reach it; only
 * the default destination changes.
 */
import { FALLBACK_SYSTEM_ROLE_LABELS } from './roleConfig';

export const ROUTE_ADMIN_DASHBOARD = '/admin/dashboard';
export const ROUTE_TLS_LANDING = '/school/skill-gaps';
export const ROUTE_TEACHER_DASHBOARD = '/dashboard';

/**
 * The user's PRIMARY role key, using the same precedence the app has always
 * applied in its inline branches: admin rights win, then district admin,
 * then classroom roles (teacher/EA), then TLS.
 *
 * Teacher/EA deliberately outrank TLS: a user holding both is a classroom
 * teacher who also specialises, and their daily work is the roster.
 *
 * @returns {'admin'|'districtAdmin'|'teacher'|'ea'|'tls'|null}
 */
export function resolvePrimaryRole(role) {
  if (!role) return null;
  if (role.admin) return 'admin';
  if (role.teacher) return 'teacher';
  if (role.ea) return 'ea';
  if (role.districtAdmin || role.districtAdminId) return 'districtAdmin';
  if (role.tls) return 'tls';
  return null;
}

/**
 * Where this user should land after authenticating (and where bare `/`,
 * `/login`, and unknown paths should send them).
 *
 * @param {object|null} role   the resolved custom-claim role object
 * @param {object} [opts]
 * @param {boolean} [opts.tlsRouteEnabled=true]  mirror of the `tlsRole`
 *        feature flag — when the TLS routes are not registered, a TLS-only
 *        user must not be sent to a route that does not exist.
 */
export function resolveLandingRoute(role, { tlsRouteEnabled = true } = {}) {
  const primary = resolvePrimaryRole(role);

  // Administrators (global or district) land on their console. A district
  // admin who ALSO teaches keeps the classroom dashboard — matching the
  // pre-existing branch, which only redirected district admins with no
  // teacher/EA claim.
  if (primary === 'admin') return ROUTE_ADMIN_DASHBOARD;
  if (primary === 'districtAdmin') return ROUTE_ADMIN_DASHBOARD;

  // TLS-only users get the TLS experience — this is the staff fix.
  if (primary === 'tls') {
    return tlsRouteEnabled ? ROUTE_TLS_LANDING : ROUTE_TEACHER_DASHBOARD;
  }

  // Teachers, EAs, and anyone role-less (legacy accounts) keep the roster.
  return ROUTE_TEACHER_DASHBOARD;
}

/** True when `/dashboard` itself should bounce this user elsewhere. */
export function shouldRedirectFromTeacherDashboard(role, opts) {
  return resolveLandingRoute(role, opts) !== ROUTE_TEACHER_DASHBOARD;
}

/**
 * Short forms for shared chrome. The configurable vocabulary
 * (FALLBACK_SYSTEM_ROLE_LABELS) is written for admin dropdowns — "TLS
 * (Teaching Learning Specialist)" and "EA (Assistant)" are far too long for
 * the sidebar caption, which is capped at 170px. These are the chrome
 * equivalents; district overrides still win when supplied.
 */
export const CHROME_ROLE_LABELS = {
  admin:         'Administrator',
  districtAdmin: 'District Admin',
  teacher:       'Teacher',
  ea:            'Assistant',
  tls:           'TLS',
};

/**
 * Label for shared chrome (the sidebar caption that used to read a
 * hardcoded "Teacher:"). `labelOverrides` accepts the same roleKey → label
 * shape resolveSystemRoleLabel consumes, so a district that renames a role
 * sees its own wording.
 */
export function resolveRoleLabel(role, labelOverrides = null) {
  const primary = resolvePrimaryRole(role);
  // Role-less legacy accounts: a neutral caption beats claiming a role.
  if (!primary) return 'Signed in as';
  return labelOverrides?.[primary]
    ?? CHROME_ROLE_LABELS[primary]
    ?? FALLBACK_SYSTEM_ROLE_LABELS[primary]
    ?? primary;
}
